/**
 * Checklist Item Activities Hook
 *
 * Fetches activity events for a single checklist item and adapts them
 * to the ActivityEvent shape consumed by the ActivityFeed UI component.
 */

import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useMemo } from "react";
import type { ChecklistItemId } from "@/packages/types";
import type { ActivityEvent } from "@/components/ui/ActivityFeed/types";
import { createActivityTransformer } from "./activityHelpers";
import { checklistActivityConfig } from "./activityConfigs/checklist-config";
import type { ChecklistActivityType } from "./activityConfigs/checklist-config";
import type { RawActivity } from "./activityConfigs/types";

const toActivityEvent = createActivityTransformer(checklistActivityConfig);

interface UseChecklistItemActivitiesReturn {
  activities: ActivityEvent[];
  isLoading: boolean;
}

export function useChecklistItemActivities(
  checklistItemId: ChecklistItemId | null,
): UseChecklistItemActivitiesReturn {
  const data = useQuery(
    api.checklist_queries.getChecklistItemActivities,
    checklistItemId ? { checklistItemId } : "skip",
  );

  const isLoading = data === undefined;

  const activities = useMemo<ActivityEvent[]>(() => {
    if (!data) return [];
    return (data as RawActivity<ChecklistActivityType>[]).map(toActivityEvent);
  }, [data]);

  return { activities, isLoading };
}
